import { useTitle } from "@/hooks/use-title.hook.ts";
import { ImportDataEditSidebar } from "@/pages/settings/import-data/edit/components/ImportDataEditSidebar.tsx";
import { ImportDataCommonSettings } from "@/pages/settings/import-data/edit/components/ImportDataCommonSettings.tsx";
import { ImportDataEditStepConfirmation } from "@/pages/settings/import-data/edit/components/ImportDataEditStepConfirmation.tsx";
import { ImportDataEditStepFinal } from "@/pages/settings/import-data/edit/components/ImportDataEditStepFinal.tsx";
import { ImportDataEditStepMatch } from "@/pages/settings/import-data/edit/components/ImportDataEditStepMatch.tsx";
import { ImportDataEditStepPreview } from "@/pages/settings/import-data/edit/components/ImportDataEditStepPreview.tsx";
import { ImportDataPresetChoice } from "@/pages/settings/import-data/edit/components/ImportDataPresetChoice.tsx";
import {
  ImportDataEditContext,
  ImportDataEditStep,
} from "@/pages/settings/import-data/edit/import-data-edit.types.ts";
import { ApiFile } from "@/types/common.types.ts";
import {
  ApiImportDataType,
  ApiImportFieldUpdateBehavior,
  ApiImportGlobalUpdateBehavior,
  ApiImportPayloadSave,
} from "@/types/import-data.types.ts";
import {
  Button,
  ConfirmationModal,
  DotSeparator,
  FullScreenPage,
  Stack,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export function ImportDataEditPage() {
  const [step, setStep] = useState<ImportDataEditStep>(ImportDataEditStep.preset);
  const [file, setFile] = useState<ApiFile | null>(null);
  const [importId, setImportId] = useState<number | undefined>(undefined);
  const [importPayload, setImportPayload] = useState<ApiImportPayloadSave | null>(null);
  const [rowsCount, setRowsCount] = useState<number>(0);
  const [importType, setImportType] = useState<ApiImportDataType>(ApiImportDataType.employee);
  const [importBehavior, setImportBehavior] = useState<ApiImportGlobalUpdateBehavior>(
    ApiImportGlobalUpdateBehavior.create_only
  );
  const [matchingBy, setMatchingBy] = useState<number | null>(null);
  const [selectedCustomTableId, setSelectedCustomTableId] = useState<number | null>(null);
  const [selectedCustomTableName, setSelectedCustomTableName] = useState<string | null>(null);
  const [defaultUpdateBehavior, setDefaultUpdateBehavior] = useState<ApiImportFieldUpdateBehavior>(
    ApiImportFieldUpdateBehavior.only_if_empty
  );
  const onNextHandlerRef = useRef<(() => void) | null>(null);
  const [isNextLoading, setNextLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openModal } = useModals();

  useTitle(t("core:settings_import_data"), t("common:settings"));

  const currentStepNumber = useMemo(() => {
    switch (step) {
      case ImportDataEditStep.preset:
        return 1;
      case ImportDataEditStep.preview:
        return 2;
      case ImportDataEditStep.common_settings:
        return 3;
      case ImportDataEditStep.match:
        return 4;
      case ImportDataEditStep.confirmation:
        return 5;
      case ImportDataEditStep.final:
        return 6;
    }
  }, [step]);

  const nextButtonText = useMemo(() => {
    switch (step) {
      case ImportDataEditStep.preset:
        return t("common:continue");
      case ImportDataEditStep.preview:
        return t("common:continue");
      case ImportDataEditStep.common_settings:
        return t("core:import_data_continue_to_matching");
      case ImportDataEditStep.match:
        return t("core:import_data_review_import");
      case ImportDataEditStep.confirmation:
        return t("core:import_data_start_import");
      case ImportDataEditStep.final:
        return t("common:done");
    }
  }, [step, t]);

  const handleExit = () => {
    openModal(ConfirmationModal, {
      question: t("common:exit"),
      text: t("core:import_data_exit_confirm"),
      acceptTitle: t("common:exit"),
      onAccept: async () => {
        navigate("/settings/import-data");
      },
    });
  };

  const handleBack = useCallback(() => {
    switch (step) {
      case ImportDataEditStep.preview:
        setStep(ImportDataEditStep.preset);
        break;
      case ImportDataEditStep.common_settings:
        setStep(ImportDataEditStep.preview);
        break;
      case ImportDataEditStep.match:
        setStep(ImportDataEditStep.common_settings);
        break;
      case ImportDataEditStep.confirmation:
        setStep(ImportDataEditStep.match);
        break;
      case ImportDataEditStep.final:
        setStep(ImportDataEditStep.confirmation);
        break;
    }
  }, [step]);

  const goToNextStep = useCallback(() => {
    switch (step) {
      case ImportDataEditStep.preset:
        setStep(ImportDataEditStep.preview);
        break;
      case ImportDataEditStep.preview:
        setStep(ImportDataEditStep.common_settings);
        break;
      case ImportDataEditStep.common_settings:
        setStep(ImportDataEditStep.match);
        break;
      case ImportDataEditStep.match:
        setStep(ImportDataEditStep.confirmation);
        break;
      case ImportDataEditStep.confirmation:
        setStep(ImportDataEditStep.final);
        break;
      case ImportDataEditStep.final:
        navigate("/settings/import-data");
        break;
    }
  }, [step, navigate]);

  const registerOnNext = useCallback((handler: (() => void) | null) => {
    onNextHandlerRef.current = handler;
  }, []);

  const handleNextClick = useCallback(() => {
    if (onNextHandlerRef.current) {
      onNextHandlerRef.current();
    } else {
      goToNextStep();
    }
  }, [goToNextStep]);

  const contextValue = useMemo(
    () => ({
      step,
      file,
      setFile,
      importId,
      setImportId,
      importPayload,
      setImportPayload,
      rowsCount,
      setRowsCount,
      importType,
      setImportType,
      importBehavior,
      setImportBehavior,
      matchingBy,
      setMatchingBy,
      selectedCustomTableId,
      setSelectedCustomTableId,
      selectedCustomTableName,
      setSelectedCustomTableName,
      defaultUpdateBehavior,
      setDefaultUpdateBehavior,
      nextButtonText,
      goToNextStep,
      onBack: handleBack,
      registerOnNext,
      isNextLoading,
      setNextLoading,
    }),
    [
      step,
      nextButtonText,
      goToNextStep,
      handleBack,
      file,
      importId,
      importPayload,
      rowsCount,
      importType,
      importBehavior,
      matchingBy,
      selectedCustomTableId,
      selectedCustomTableName,
      defaultUpdateBehavior,
      registerOnNext,
      isNextLoading,
    ]
  );

  const renderStepContent = () => {
    switch (step) {
      case ImportDataEditStep.preset:
        return <ImportDataPresetChoice />;
      case ImportDataEditStep.preview:
        return <ImportDataEditStepPreview />;
      case ImportDataEditStep.common_settings:
        return <ImportDataCommonSettings />;
      case ImportDataEditStep.match:
        return <ImportDataEditStepMatch />;
      case ImportDataEditStep.confirmation:
        return <ImportDataEditStepConfirmation />;
    }
  };

  return (
    <ImportDataEditContext.Provider value={contextValue}>
      {step !== ImportDataEditStep.final ? (
        <FullScreenPage
          backSlot={
            <Button variant="secondary" onClick={handleExit} size={"lg"}>
              {t("common:exit")}
            </Button>
          }
          titleSlot={
            <DotSeparator>
              <Text className={"font-semibold"}>{t("core:settings_import_data")}</Text>
              <Text className="text-secondary">
                {t("core:step_of", { currentStep: currentStepNumber, countSteps: 5 })}
              </Text>
            </DotSeparator>
          }
          actionsSlot={
            <Stack direction="horizontal" gap="sm">
              {step !== ImportDataEditStep.preset && (
                <Button variant="secondary" onClick={handleBack} size={"lg"}>
                  {t("common:back")}
                </Button>
              )}
              <Button onClick={handleNextClick} size={"lg"} isLoading={isNextLoading}>
                {nextButtonText}
              </Button>
            </Stack>
          }
          sidebarSlot={<ImportDataEditSidebar step={step} onStepClick={setStep} />}
          isCenterVertical={false}
        >
          {renderStepContent()}
        </FullScreenPage>
      ) : (
        <ImportDataEditStepFinal />
      )}
    </ImportDataEditContext.Provider>
  );
}
